import React from "react";
import {
  Stack,
  Heading,
  Text,
  Divider,
  Box,
  Button,
  TextLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { EICFormData } from "./types";
import { useFormValidation } from "./useFormValidation";
import { capitalizeFirstLetter, getHasElectricity, getIsVIC } from "./helper";
import { useConnectionFeesAndTariffs } from "./hooks/useConnectionFeesAndTariffs";

interface EICFormPageFourProps {
  onBack: () => void;
  onContinue: () => void;
}

const EICFormPageFour: React.FC<EICFormPageFourProps> = ({
  onBack,
  onContinue,
}) => {
  const { values } = useFormikContext<EICFormData>();
  const { isValidating, handleContinueClick } = useFormValidation(onContinue);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleContinueClick();
  };

  // Determine if this is a business account
  const isBusinessAccount = values.accountType === "business";
  const hasElectricity = getHasElectricity(values.connectionTypes);
  const isVIC = getIsVIC(values.address);

  const { tariffsData } = useConnectionFeesAndTariffs({
    referenceNumber: values.referenceNumber,
    accountType: values.accountType,
    autoFetch: true,
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack
        space={["large", "xlarge"]}
        data-role="section"
        id="eic-form-page-four"
      >
        {/* Content */}
        <Stack>
          <Heading variant="h3">Energy charges</Heading>
          <Text>
            We will set you up on our Standing Agreement and your prices will be
            our Standing rates. You can find these at{" "}
            <TextLink href="https://originenergy.com.au/pricing">
              originenergy.com.au/pricing
            </TextLink>
            .
          </Text>
          <Text>
            The charges that will apply to you will depend on your metering set
            up and distributor and won’t be confirmed until after your
            connection is complete.
          </Text>
        </Stack>
        {/* Content */}

        {/* Content */}
        {hasElectricity && (
          <Stack>
            <Heading variant="h4">How your Standing Agreement compares</Heading>
            {isVIC ? (
              <Stack>
                <Text>
                  For electricity, the Victorian Default Offer reference price
                  is a rate set by the regulator for electricity in each area.
                  Energy companies compare prices for electricity in an area
                  against the reference price.
                </Text>
                <Text>
                  The tables below show the estimated annual cost of this
                  Standing Agreement for each network area for each tariff type
                  for which there is a Victorian Default Offer, based on an
                  estimated annual usage. Our standing rates can change from
                  time to time, generally once a year in July. When this happens
                  the estimated annual cost below will also change.
                </Text>
                <Text>
                  Under this Standing Agreement the usage and supply charges are
                  0% less than the Victorian Default Offer reference price (so
                  the rates are equal to that price). Note that bills vary with
                  usage.
                </Text>
              </Stack>
            ) : (
              <Stack>
                <Text>
                  If your Supply Address is in NSW, QLD, ACT or SA, the
                  reference price is a rate set by the regulator for electricity
                  in each area. Energy companies compare prices for electricity
                  in an area against the reference price.
                </Text>
                <Text>
                  The tables below show the estimated annual cost of this
                  Standing Agreement for each network area for each tariff type
                  for which there is a reference price, based on an estimated
                  annual usage. Our standing rates can change from time to time,
                  generally once a year around July or August. When this happens
                  the estimated annual cost below will also change.
                </Text>
                <Text>
                  Under this Standing Agreement the usage and supply charges are
                  0% less than the reference price (so the rates are equal to
                  that price). Note that bills vary with usage.
                </Text>
              </Stack>
            )}
          </Stack>
        )}
        {/* Content */}

        {/* Tariffs */}
        {hasElectricity && (
          <Stack space={["large", "xlarge"]}>
            <Heading variant="h4">
              {isBusinessAccount ? "Business" : "Residential"} tariffs
            </Heading>
            <Stack>
              <Table
                bordered
                stickyHeader={true}
                hover={true}
                size="small"
                textVariant="body-small"
              >
                <TableHead backgroundColor="secondaryB">
                  <TableRow>
                    <TableCell>Distributor</TableCell>
                    <TableCell>Tariff</TableCell>
                    <TableCell>Estimated annual usage (kWh)</TableCell>
                    <TableCell>Estimated annual cost (incl GST)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tariffsData?.map((tariff) => (
                    <TableRow key={tariff.distributor}>
                      <TableCell>{tariff.distributor}</TableCell>
                      <TableCell>
                        {capitalizeFirstLetter(tariff.tariff_type)}
                      </TableCell>
                      <TableCell>
                        {Number(
                          tariff.estimated_annual_usage_kwh
                        ).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        $
                        {Number(
                          tariff.estimated_annual_cost_incl_gst
                        ).toLocaleString("en-AU", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Text variant="body-small" tone="neutralLight">
                Energy Fact Sheets containing all the key details about the
                offer are available upon request or at{" "}
                <TextLink href="https://originenergy.com.au/pricing">
                  originenergy.com.au/pricing
                </TextLink>{" "}
                for generally available plans.
              </Text>
            </Stack>
          </Stack>
        )}
        {/* End Tariffs */}

        <Divider />

        {/* Actions  */}
        <Box display="flex" justifyContent="space-between">
          <Button variant="outlined" onClick={onBack} type="button">
            Back
          </Button>
          <Button onClick={handleContinueClick} spinner={isValidating}>
            Continue
          </Button>
        </Box>
        {/* End Actions */}
      </Stack>
    </form>
  );
};

export default EICFormPageFour;
